import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { toast } from 'react-hot-toast';

// API Configuration - point to deployed backend for local testing
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'https://case-intake-195480669181.us-central1.run.app';

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request ID for tracking
    config.headers['X-Request-ID'] = crypto.randomUUID();

    // Add tenant ID for V2 multi-tenant endpoints
    // Phase 0 stub: uses dev default if not set; Phase 2 will derive from JWT
    const tenantId = localStorage.getItem('tenantId') || '00000000-0000-0000-0000-000000000001';
    config.headers['X-Tenant-ID'] = tenantId;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    // Handle common error scenarios
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You do not have permission for this action.');
    } else if (error.response?.status === 404) {
      toast.error('Resource not found.');
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout. Please try again.');
    } else if (!error.response) {
      toast.error('Network error. Please check your connection.');
    }

    return Promise.reject(error);
  }
);

// Helper functions
export const handleApiError = (error: any): string => {
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  } else if (error.response?.data?.message) {
    return error.response.data.message;
  } else if (error.message) {
    return error.message;
  } else {
    return 'An unexpected error occurred';
  }
};

export const isApiError = (error: any): boolean => {
  return error?.response?.status >= 400;
};

// File upload helper
export const uploadFile = async (
  url: string,
  file: File,
  fields: Record<string, string>,
  onProgress?: (percentage: number) => void
): Promise<void> => {
  const formData = new FormData();
  
  // Add all fields first
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  
  // Add file last
  formData.append('file', file);

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent: any) => {
      if (progressEvent.lengthComputable && onProgress) {
        const percentage = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentage);
      }
    },
  };

  await axios.post(url, formData, config);
};

// SSE (Server-Sent Events) helper
export class SSEClient {
  private eventSource: EventSource | null = null;
  private url: string;
  private listeners: Map<string, (data: any) => void> = new Map();

  constructor(url: string) {
    this.url = url;
  }

  connect(): void {
    if (this.eventSource) {
      this.disconnect();
    }

    this.eventSource = new EventSource(this.url);

    this.eventSource.onopen = () => {
      console.log('SSE connection opened');
    };

    this.eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      // Auto-reconnect after 5 seconds
      setTimeout(() => {
        if (this.eventSource?.readyState === EventSource.CLOSED) {
          this.connect();
        }
      }, 5000);
    };

    this.eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.listeners.get('message')?.(data);
      } catch (error) {
        console.error('Failed to parse SSE message:', error);
      }
    };

    // Handle custom events
    this.listeners.forEach((callback, eventType) => {
      if (eventType !== 'message' && this.eventSource) {
        this.eventSource.addEventListener(eventType, (event: any) => {
          try {
            const data = JSON.parse(event.data);
            callback(data);
          } catch (error) {
            console.error(`Failed to parse SSE event ${eventType}:`, error);
          }
        });
      }
    });
  }

  disconnect(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  addEventListener(eventType: string, callback: (data: any) => void): void {
    this.listeners.set(eventType, callback);
    
    if (this.eventSource && eventType !== 'message') {
      this.eventSource.addEventListener(eventType, (event: any) => {
        try {
          const data = JSON.parse(event.data);
          callback(data);
        } catch (error) {
          console.error(`Failed to parse SSE event ${eventType}:`, error);
        }
      });
    }
  }

  removeEventListener(eventType: string): void {
    this.listeners.delete(eventType);
  }

  isConnected(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN;
  }
}

// Export configured axios instance
export default api;
